import FormSchema from '@/utils/LeadFormSchema'
import {get, cloneDeep} from 'lodash'
import {constants} from '@/utils/Constants.js'
import {filterFormQuestions} from '@/utils/filterFormQuestions.js'

/**
 * Set the impression tracker value
 */
function setImpressionTrackerValue (ad, duplicating) {
  const impressionTrackerDefaultValue = [
    {
      type: 'url',
      value: null
    }
  ]

  if (ad.external_impressions === null || ad.external_impressions === '') {
    return impressionTrackerDefaultValue
  }

  const external_impressions = JSON.parse(ad.external_impressions)

  if (!external_impressions.length) {
    return impressionTrackerDefaultValue
  }

  return external_impressions.map(tracker => {
    tracker.ad_id = duplicating ? 0 : ad.id
    return tracker
  })
}

function composeStep6 (campaign) {
  let step6 = []

  if (campaign.objective_id === constants.CAMPAIGN_OBJECTIVES_IDS.LEAD_FORM) {
    step6 = campaign.lead_form || cloneDeep(FormSchema)

    step6.form.properties = {
      primitive: step6.form ? filterFormQuestions(step6.form.properties, false) : [],
      custom: step6.form ? filterFormQuestions(step6.form.properties, true) : []
    }
  }

  return step6
}

export default function (campaign, duplicating = false) {
  const COST_TYPE_MAP = {
    1: 'impressions',
    2: 'clicks',
    3: 'videos',
    4: 'actions'
  }

  const no_end_date = campaign.end_date === null ? 'true' : 'false'

  return {
    step1: {
      advertisingChannelObject: campaign.advertising_channel,
      advertisingChannel: campaign.advertising_channel ? campaign.advertising_channel.title : 'N/A',
      objective: campaign.objective ? campaign.objective.display_name : 'N/A',
      type: campaign.display_type ? campaign.display_type.display_name : 'N/A',
      advertising_channel_id: campaign.advertising_channel_id,
      objective_id: campaign.objective_id,
      type_id: campaign.display_type_id
    },
    step2: {
      name: campaign.name,
      brand: campaign.branding_text,
      industry_id: campaign.campaign_category_id,
      assignee_id: null,
      once_it_approved: campaign.start_date === null ? 'true' : 'false',
      no_end_date,
      start_date: duplicating ? (new Date).toISOString().split('T')[0] : campaign.start_date,
      end_date: campaign.end_date,
      main_event: campaign.main_conversion ? campaign.main_conversion.name : null,
      objective: campaign.objective
    },
    step3: {
      advertising_channel_id: campaign.advertising_channel_id,
      adsets: campaign.adsets.map((adset) => {
        return {
          campaign_user_id: campaign.user_id,
          campaign_account_id: campaign.account_id,
          id: duplicating ? 0 : adset.id,
          name: adset.name,
          language: adset.language_id,
          new_audience: true,
          new_audience_name: '', // The audience naming shall be unique per logging in account
          delivery_type: adset.delivery_type,
          locations: {
            countries: adset.countries.map(i => i.id),
            cities: adset.cities.map(i => i.id)
          },
          isps: adset.formatted_isps.map(i => i.id),
          content_categories: adset.active_content_categories.map(i => i.id),
          keywords: adset.keywords.map(i => i.name),
          target_devices: adset.devices.length ? adset.devices.map(i => i.id) : [1, 2, 3],
          target_age_range: campaign.advertising_channel.supports_age_range_targeting ? [adset.age_min, adset.age_max] : [0, 0],
          target_genders: adset.genders.length ? adset.genders.map(i => i.id) : [],
          target_platforms: {
            websites: campaign.advertising_channel.supports_native_publisher_targeting ? adset.domains.filter(i => i.type === 1 || i.type === 4).map(i => i.id) : [],
            apps: adset.domains.filter(i => i.type === 2 || i.type === 3).map(i => i.id),
            purgeable_disallowed_domains_history: [],
            include_new_platforms: !!adset.include_new_websites
          },
          meta_detailed_targeting: (campaign.advertising_channel.supports_meta_detailed_targeting && adset.meta_targeting.length) ? adset.meta_targeting.map(i => i.id) : [],
          budget: {
            optimizing_for: COST_TYPE_MAP[adset.cost_type.id], // impressions, clicks, videos, actions
            action_type: null, // Lead, Signup, Add to cart, Add to wish list, Purchase
            cost_per_action: parseFloat(adset.max_bid_price).toFixed(3),
            fallback_cpc: parseFloat(adset.cpa_max_cpc_bid || 0).toFixed(3),
            budget_value: no_end_date !== 'true' ? parseFloat(adset.budget).toFixed(2) : parseFloat(adset.daily_budget).toFixed(2),
            custom_bids: duplicating ? [] : adset.custom_bids.map((domain) => {
              return {
                id: domain.id,
                bid: domain.pivot.max_bid
              }
            })
          },
          frequency_capping: adset.frequency_capping,
          // audience_id: 0,
          hasError: false,
          is_duplicating: duplicating,
          ads: adset.ads.map((ad) => {
            if (ad.id === 0) {
              return ad
            }

            // the ad is from the database
            const adFormatted = JSON.parse(ad.frontend_meta === null ? '{}' : ad.frontend_meta)
            adFormatted.id = duplicating ? 0 : ad.id
            adFormatted.url = ad.landing_page
            // adFormatted.file = JSON.parse(ad.meta || '{}').s3 || null
            adFormatted.content = ad.title
            adFormatted.is_story = !!parseInt(adFormatted.is_story)
            adFormatted.has_phone = !!parseInt(adFormatted.has_phone)
            adFormatted.has_video = !!parseInt(adFormatted.has_video)
            adFormatted.upload_video = !!parseInt(adFormatted.upload_video)
            adFormatted.upload_enabled = !!parseInt(adFormatted.upload_enabled)
            adFormatted.hasError = false
            adFormatted.is_previewing = false
            adFormatted.activation_status = duplicating ? null : ad.activation_status
            adFormatted.acceptance_status = duplicating ? null : ad.acceptance_status
            adFormatted.comment = ad.rejection_comment
            adFormatted.impressions_tracker = setImpressionTrackerValue(ad, duplicating)
            adFormatted.audience_suggestions =  get(ad, 'audience_suggestion.suggestions', { genders: [], age_groups: [] })
            adFormatted.show_audience_suggestions = Boolean(duplicating === true)
            adFormatted.want_to_update = false
            adFormatted.cta = ad.cta
            adFormatted.feedback = ad.feedback

            // parse the returned data from db to match the type
            if (adFormatted.audience_suggestions.hasOwnProperty('age_groups')) {
              adFormatted.audience_suggestions.age_groups = adFormatted.audience_suggestions.age_groups.map(i => parseInt(i))
            } else {
              adFormatted.audience_suggestions.age_groups = []
            }

            // parse the returned data from db to match the type
            if (adFormatted.audience_suggestions.hasOwnProperty('genders')) {
              adFormatted.audience_suggestions.genders = adFormatted.audience_suggestions.genders.map(i => parseInt(i))
            } else {
              adFormatted.audience_suggestions.genders = []
            }

            return adFormatted
          })
        }
      })
    },
    step6: composeStep6(campaign)
  }
}
